import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'http://localhost:8000/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' } };
};

const OrgChartNode = ({ node, expandedNodes, toggleExpand, depth = 0 }) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;

    const nodeCard = {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderLeft: `4px solid ${depth === 0 ? '#2563eb' : depth === 1 ? '#7c3aed' : '#0ea5e9'}`,
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '8px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    };

    const unitBadge = {
        display: 'inline-block', padding: '1px 8px', borderRadius: '999px',
        fontSize: '0.7rem', fontWeight: '600', backgroundColor: '#e0e7ff', color: '#3730a3', marginLeft: '8px',
    };

    const positionPill = (pos) => ({
        display: 'inline-block', padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '500',
        marginRight: '6px', marginBottom: '4px', cursor: 'default',
        backgroundColor: pos.is_vacant ? '#fee2e2' : (pos.is_manager_position ? '#fef3c7' : '#f1f5f9'),
        color: pos.is_vacant ? '#991b1b' : (pos.is_manager_position ? '#92400e' : '#334155'),
        border: pos.is_manager_position ? '1px solid #f59e0b' : '1px solid #e2e8f0',
    });

    return (
        <div style={{ marginLeft: depth > 0 ? '28px' : '0' }}>
            {depth > 0 && (
                <div style={{ borderLeft: '2px solid #e2e8f0', height: '12px', marginLeft: '16px' }} />
            )}
            <div style={nodeCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    {hasChildren && (
                        <button
                            onClick={() => toggleExpand(node.id)}
                            style={{
                                width: '22px', height: '22px', borderRadius: '4px', border: '1px solid #e2e8f0',
                                backgroundColor: '#f8fafc', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700',
                                color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                padding: 0, lineHeight: 1,
                            }}
                        >
                            {isExpanded ? '−' : '+'}
                        </button>
                    )}
                    <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>{node.name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{node.code}</span>
                    {node.unit_type && <span style={unitBadge}>{node.unit_type}</span>}
                    {hasChildren && (
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: 'auto' }}>
                            {node.children.length} sub-unit{node.children.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                <div style={{ fontSize: '0.8rem', color: '#475569', marginBottom: node.positions && node.positions.length > 0 ? '8px' : 0 }}>
                    Manager:{' '}
                    {node.manager_position ? (
                        <span style={{ color: node.manager_position.is_vacant ? '#dc2626' : '#16a34a', fontWeight: '600' }}>
                            {node.manager_position.title}{node.manager_position.is_vacant ? ' (Vacant)' : ''}
                        </span>
                    ) : (
                        <span style={{ color: '#dc2626', fontWeight: '600' }}>None assigned</span>
                    )}
                </div>

                {node.positions && node.positions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                        {node.positions.map(pos => (
                            <span
                                key={pos.id}
                                style={positionPill(pos)}
                                title={pos.reports_to && pos.reports_to.length > 0 ? `Reports to: ${pos.reports_to.map(r => r.title).join(', ')}` : 'No reporting line set'}
                            >
                                {pos.title}
                                {pos.level ? ` · ${pos.level}` : ''}
                                {pos.is_vacant ? ' (Vacant)' : ''}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {hasChildren && isExpanded && (
                <div style={{ borderLeft: '2px solid #e2e8f0', marginLeft: '16px', paddingLeft: '0' }}>
                    {node.children.map(child => (
                        <OrgChartNode key={child.id} node={child} expandedNodes={expandedNodes} toggleExpand={toggleExpand} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

const OrgChart = () => {
    const navigate = useNavigate();
    const companyId = localStorage.getItem('selectedCompanyId') || 1;

    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    const handleError = (err) => {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); return true;
        }
        return false;
    };

    useEffect(() => {
        const fetchChart = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_BASE}/companies/${companyId}/org-chart`, getAuthHeaders());
                const data = res.data.data || res.data || [];
                setTree(data);
                // Auto-expand root nodes
                const rootIds = new Set(data.map(n => n.id));
                setExpandedNodes(rootIds);
                setError(null);
            } catch (err) {
                if (!handleError(err)) setError('Failed to load organizational chart.');
            } finally {
                setLoading(false);
            }
        };
        fetchChart();
    }, [companyId]);

    const toggleExpand = (nodeId) => {
        setExpandedNodes(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
            return next;
        });
    };

    const expandAll = (nodes) => {
        const ids = new Set();
        const collect = (list) => { list.forEach(n => { ids.add(n.id); if (n.children) collect(n.children); }); };
        collect(nodes);
        setExpandedNodes(ids);
    };

    const collapseAll = () => setExpandedNodes(new Set());

    const handleExport = () => {
        const blob = new Blob([JSON.stringify(tree, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `org-chart-company-${companyId}.json`; a.click();
        URL.revokeObjectURL(url);
    };

    // Count total nodes
    const countNodes = (nodes) => nodes.reduce((sum, n) => sum + 1 + (n.children ? countNodes(n.children) : 0), 0);
    const totalUnits = countNodes(tree);

    // Filter tree by search
    const filterTree = (nodes, term) => {
        if (!term) return nodes;
        const s = term.toLowerCase();
        return nodes.reduce((acc, node) => {
            const matches = (node.name || '').toLowerCase().includes(s) || (node.code || '').toLowerCase().includes(s);
            const filteredChildren = node.children ? filterTree(node.children, term) : [];
            if (matches || filteredChildren.length > 0) {
                acc.push({ ...node, children: filteredChildren.length > 0 ? filteredChildren : (matches && node.children ? node.children : []) });
            }
            return acc;
        }, []);
    };

    const displayTree = filterTree(tree, searchTerm);

    // ── Styles ──
    const pageStyle = { padding: '24px 32px', backgroundColor: '#f1f5f9', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
    const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' };
    const titleStyle = { fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: 0 };
    const btnPrimary = { padding: '8px 16px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' };
    const btnOutline = { padding: '8px 16px', backgroundColor: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' };
    const searchBox = { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', width: '280px', outline: 'none' };
    const infoBar = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' };

    if (initialLoading) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#64748b', marginTop: '60px' }}>Loading organizational chart...</p></div>;
    if (error) return <div style={pageStyle}><p style={{ textAlign: 'center', color: '#dc2626', marginTop: '60px' }}>{error}</p></div>;

    return (
        <div style={pageStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <h1 style={titleStyle}>Organizational Chart</h1>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={btnOutline} onClick={() => expandAll(tree)}>Expand All</button>
                    <button style={btnOutline} onClick={collapseAll}>Collapse All</button>
                    <button style={btnPrimary} onClick={handleExport}>📥 Export JSON</button>
                </div>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '16px', marginTop: '-12px' }}>
                Shows units, positions, hierarchy, and reporting lines only — no employee names or salaries.
                Hover a position pill to see who it reports to.
            </p>

            {/* Info bar */}
            <div style={infoBar}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{totalUnits} unit{totalUnits !== 1 ? 's' : ''} in hierarchy</span>
                <input style={searchBox} placeholder="Search units by name or code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>

            {/* Tree */}
            {displayTree.length === 0 ? (
                <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#94a3b8', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    {searchTerm ? 'No units match your search.' : 'No departments found for this company yet.'}
                </div>
            ) : (
                <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    {displayTree.map(node => (
                        <OrgChartNode key={node.id} node={node} expandedNodes={expandedNodes} toggleExpand={toggleExpand} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default OrgChart;